import {
    DeviceOwner,
    Port,
} from 'cloud-networks/modules/PortsModule/PortsModule.types';
import {
    Subnet,
    SubnetApiEntity,
} from 'cloud-networks/modules/SubnetModule/SubnetModule.types';
import { Netmask } from 'netmask/lib/netmask';
import * as R from 'ramda';
import { getStore } from 'store/store';
import { arrayHelper } from 'utils/arrayHelper';
import { randomHelper } from 'utils/randomHelper';

import { WhiteLabelSelectors } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule';
import { Validation } from 'reactComponents/Forms/Validation/Validation';

export const DEFAULT_DNS_LIST = ['8.8.8.8', '8.8.4.4'];

const handleSubnet = (item) => {
    let pools = '';
    if (item.allocation_pools) {
        item.allocation_pools.forEach((pool, index) => {
            if (index > 0) {
                pools += '; ';
            }

            pools += pool.start + ' - ' + pool.end;
        });
    }

    return {
        id: item.id,
        name: item.name,
        cidr: item.cidr,
        gateway: item.gateway_ip,
        dhcp: item.enable_dhcp,
        ipPool: pools,
        dns: item.dns_nameservers,
        hostRoutes: getHostRoutes(item),
    };
};

const handleParams = (item, isEdit?: boolean) => {
    const pools = item.ipPool ? item.ipPool.split(';') : [];
    const allocationPools = [] as {
        start: any;
        end: any;
    }[];
    pools.forEach((pool) => {
        const [start, end] = pool.split('-').map((item) => item.trim());
        const range = start && end ? { start, end } : undefined;

        if (range) {
            allocationPools.push(range);
        }
    });

    return {
        name: item.name,
        gateway_ip: !isEdit ? item.gateway || undefined : undefined,
        enable_dhcp: item.dhcp,
        dns_nameservers: item.dns || [],
        host_routes: getHostRoutesParams(item),
        allocation_pools: allocationPools,
    };
};

const handleSubnets = (subnets) =>
    subnets.map((item) => {
        const params = handleParams(item);

        return {
            ...params,
            cidr: item.cidr,
            ip_version: 4,
        };
    });

const isIp = (ip) => {
    return /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/([0-9]|[1-2][0-9]|3[0-2]))$/.test(
        ip,
    );
};

const _findFreeNetmask = (busyCidrsList) => {
    const cidrLast = busyCidrsList[busyCidrsList.length - 1];
    const block = new Netmask(cidrLast);
    const blockNext = block.next();

    if (
        Validation.Yup.string()
            .cidrSubnet()
            .isValidSync(blockNext.toString())
    ) {
        return blockNext;
    }
    let netmask = new Netmask(defaultCidr);
    let cidr = netmask.toString();
    let netmaskNext;

    do {
        if (
            Validation.Yup.string()
                .cidrSubnet()
                .isValidSync(cidr) &&
            !busyCidrsList.includes(cidr)
        ) {
            return netmask;
        }
        netmaskNext = netmask.next();
        cidr = netmaskNext.toString();
        netmask = new Netmask(cidr);
    } while (true);
};

const defaultCidr =
    WhiteLabelSelectors.getDefaultCidr(getStore()) || '10.0.0.0/24';

function getDefaultSubnet(isEnableDHCP = true, dns = []): Subnet {
    const block = new Netmask(defaultCidr);

    return {
        name: `subnet_${randomHelper.getRandomInt(1000, 9999)}`,
        cidr: defaultCidr,
        gateway: block.first,
        dhcp: isEnableDHCP,
        ipPool: getIpPool(block),
        dns,
        id: String(randomHelper.getRandomInt(1000, 9999)),
    };
}

const getInitialValues = (
    busyCidrsList,
    isEnableDHCP = true,
    dns = [],
): Subnet => {
    const busyCidrsListCount = busyCidrsList.length;
    if (busyCidrsListCount === 0) {
        return getDefaultSubnet(isEnableDHCP, dns);
    }
    const netmask = _findFreeNetmask(busyCidrsList);
    const gateway = netmask.first;

    return {
        ...getDefaultSubnet(isEnableDHCP, dns),
        cidr: netmask.toString(),
        gateway,
        ipPool: getIpPool(netmask),
    };
};

const getSubnetValuesByCidr = ({ cidr }) => {
    const netmask = new Netmask(cidr);
    const gateway = netmask.first;

    return {
        ...getDefaultSubnet(),
        cidr: netmask.toString(),
        gateway,
        ipPool: getIpPool(netmask),
    };
};

function getHostRoutes(item) {
    const hostRoutes: string[] = [];
    item.host_routes.forEach((route) => {
        hostRoutes.push(route.destination + ' - ' + route.nexthop);
    });

    return hostRoutes.join('\n');
}

function getHostRoutesParams(item) {
    const hostRotes = item.hostRoutes ? item.hostRoutes.split('\n') : [];

    return hostRotes
        .map((route) => {
            const [destination, nexthop] = route
                .split('-')
                .map((item) => item.trim());
            if (destination && nexthop) {
                return {
                    destination,
                    nexthop,
                };
            }
            return undefined;
        })
        .filter((item) => item);
}

function ip2bin(ip) {
    return ip
        .split('.')
        .map((item) => {
            const bin = Number(item).toString(2);

            return `${new Array(8 - bin.length).fill('0').join('')}${bin}`;
        })
        .join('');
}

function bin2ip(bin) {
    const binIp = `${new Array(32 - bin.length).fill('0').join('')}${bin}`;

    // @ts-ignore
    return binIp
        .match(/\d{8}/g)
        .map((item) => parseInt(item, 2))
        .join('.');
}

function getIpPool(netmask, busyIp?: any) {
    const gateway = busyIp || netmask.first;
    const second = bin2ip((parseInt(ip2bin(gateway), 2) + 1).toString(2));

    return `${second} - ${netmask.last}`;
}

const sortCidrsList = (cidrsList) => {
    return cidrsList.sort((a, b) => {
        const maxIpA = new Netmask(a).last;
        const maxIpB = new Netmask(b).last;

        return (
            Number.parseInt(ip2bin(maxIpA), 2) -
            Number.parseInt(ip2bin(maxIpB), 2)
        );
    });
};

const getBusyCidrs = ({ routerId, portsList, subnetsList }): string[] => {
    const routerPortsList = portsList
        .filter((port) => routerId === port.device_id)
        .map((port) => port.fixed_ips);

    const routerSubnetsList = routerPortsList.map((arr) => arr[0].subnet_id);

    return R.uniq(
        routerSubnetsList
            .map((id) => {
                const subnet = subnetsList.find((item) => item.id === id);

                return subnet ? subnet.cidr : null;
            })
            .filter((cidr) =>
                Validation.Yup.string()
                    .cidrSubnet()
                    .isValidSync(cidr),
            ),
    );
};

const getSubnetByRouter = ({ id, portsList, subnetsList }): Subnet => {
    if (!id) {
        return getInitialValues([]);
    }

    const cidrsList = getBusyCidrs({
        routerId: id,
        portsList,
        subnetsList,
    });
    const sortedCidrsList = sortCidrsList(cidrsList);

    return getInitialValues(sortedCidrsList);
};

function getListByNetworkId(list: SubnetApiEntity[], networkId: string) {
    return list.filter((item) => item.network_id === networkId);
}

function getListByPorts(list: SubnetApiEntity[], ports: Port[]) {
    const subnetIds = ports.flatMap((port) =>
        port.fixed_ips.flatMap((ip) => ip.subnet_id || []),
    );
    return list.filter((item) => subnetIds.includes(item.id));
}

function hasDhcp(subnet: SubnetApiEntity | SubnetApiEntity[]) {
    const subnets = arrayHelper.singleToArray(subnet);
    return subnets.every((item) => item.enable_dhcp);
}

function hasNetworkDhcp(allSubnets: SubnetApiEntity[], networkId: string) {
    return hasDhcp(getListByNetworkId(allSubnets, networkId));
}

function getDnsIps(subnet: Subnet | SubnetApiEntity) {
    const dns =
        (subnet as Subnet).dns || (subnet as SubnetApiEntity).dns_nameservers;

    return dns.length ? dns : null;
}

function getPrivateDnsIps({
    portList,
    subnet,
}: {
    portList: Port[];
    subnet: Subnet | SubnetApiEntity;
}) {
    const privateDnsIps = portList
        .filter(
            (port) =>
                port.device_owner === DeviceOwner.dhcp &&
                port.fixed_ips.some(
                    (fixedIp) => fixedIp.subnet_id === subnet.id,
                ),
        )
        .flatMap((port) =>
            port.fixed_ips
                .filter((fixedIp) => fixedIp.subnet_id === subnet.id)
                .map((fixedIp) => fixedIp.ip_address),
        );

    return privateDnsIps.length ? privateDnsIps : null;
}

export const SubnetUtils = {
    handleSubnet,
    handleParams,
    handleSubnets,
    isIp,
    getDnsIps,
    getIpPool,
    getInitialValues,
    getSubnetValuesByCidr,
    sortCidrsList,
    getBusyCidrs,
    getSubnetByRouter,
    getListByNetworkId,
    getListByPorts,
    hasNetworkDhcp,
    getPrivateDnsIps,
};
